import {createTheme} from '@mui/material';

/**
 * Shared typography definitions with Outfit & Inter fallback font stack.
 */
const sharedTypography = {
    fontFamily: 'Outfit, Inter, system-ui, -apple-system, sans-serif',
    h3: {fontWeight: 900},
    h4: {fontWeight: 900},
    h5: {fontWeight: 800},
    h6: {fontWeight: 800},
    body1: {fontWeight: 500},
    button: {textTransform: 'none', fontWeight: 700},
};

/**
 * Shared border radius shape definitions.
 */
const sharedShape = {borderRadius: 16};

/**
 * Generates custom button overrides with glowing hover animations.
 *
 * @param {string} glowColor - RGBA shadow string for hover glow.
 * @returns {object} MUI style overrides.
 */
const sharedButtonOverrides = (glowColor) => ({
    borderRadius: 12,
    padding: '8px 20px',
    boxShadow: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 6px 20px ${glowColor}`,
    },
});

/**
 * Generates glassmorphic paper panel styling overrides.
 *
 * @param {string} bgColor - Background color string with alpha.
 * @param {string} borderColor - Border color string with alpha.
 * @returns {object} MUI Paper style overrides.
 */
const sharedPaperOverrides = (bgColor, borderColor) => ({
    borderRadius: 16,
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
    backgroundImage: 'none',
    backgroundColor: bgColor,
    backdropFilter: 'blur(16px)',
    border: `1px solid ${borderColor}`,
});

/**
 * Light theme configuration palette and components.
 */
export const lightThemeOptions = {
    palette: {
        mode: 'light',
        primary: {main: '#00c853'},
        secondary: {main: '#ff6d00'},
        background: {default: '#f3fcf4', paper: '#ffffff'},
        text: {primary: '#092518', secondary: '#4e6559'},
    },
    typography: sharedTypography,
    shape: sharedShape,
    components: {
        MuiButton: {
            styleOverrides: {
                root: sharedButtonOverrides('rgba(0, 200, 83, 0.2)'),
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: sharedPaperOverrides('#ffffff', 'rgba(0, 0, 0, 0.08)'),
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
                },
            },
        },
    },
};

/**
 * Dark theme configuration with high-contrast gaming palette and custom scrollbars.
 */
export const darkThemeOptions = {
    palette: {
        mode: 'dark',
        primary: {main: '#00e676'},
        secondary: {main: '#ff9100'},
        background: {default: '#070b19', paper: '#0f162e'},
        text: {primary: '#f1f5f9', secondary: '#94a3b8'},
    },
    typography: sharedTypography,
    shape: sharedShape,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    scrollbarColor: '#00e676 rgba(0,0,0,0.1)',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(0,0,0,0.15)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#00e676',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#00b248',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: sharedButtonOverrides('rgba(0, 230, 118, 0.3)'),
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: sharedPaperOverrides('rgba(15, 22, 46, 0.75)', 'rgba(255, 255, 255, 0.08)'),
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                },
            },
        },
    },
};

/**
 * Theme factory creating an MUI Theme instance based on dark mode state.
 *
 * @param {boolean} darkMode - True for dark mode, false for light mode.
 * @returns {object} Created MUI theme instance.
 */
export function getTheme(darkMode) {
    return createTheme(darkMode ? darkThemeOptions : lightThemeOptions);
}

export default getTheme;
